import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import { mapJournalEntry } from '../journal/journalMapping';
import type { IVatService, VatReadOptions } from './IVatService';
import { VatEndpoints } from './vatEndpoints';
import { mapVatReport, type VatReportWire } from './vatMapping';
import type { BuildVatSettlementRequest } from './requests';
import type {
    GetVatReportResponse,
    PrepareVatSettlementResponse,
} from './responses';

type VatServiceOptions = {
    baseUrl: string;
};

/**
 * A body this client could not read as a VAT report is contract drift, not a
 * figure to guess at. Mirrors `StatementsService`'s `UNMAPPABLE_STATEMENT`: the
 * screen's error-with-retry state is the correct outcome, and a great deal safer
 * than rendering a partial report — or a zeroed one — as though it were the
 * position.
 */
const UNMAPPABLE_REPORT: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message: 'The server returned a VAT report this client cannot read.',
        code: 'UNMAPPABLE_VAT_REPORT',
    },
};

const DEFAULT_OPTIONS: VatReadOptions = { locale: 'en' };

/**
 * The live ledger VAT client (bos-api).
 *
 * Both methods map at the boundary — the report through `vatMapping`, the
 * settlement draft through the journal's own `mapJournalEntry`, because the
 * settlement endpoint returns a plain `JournalEntryResponse` and a second
 * journal mapper would be free to disagree with the first.
 *
 * What this service deliberately does **not** do:
 *
 * - It does not classify. Which accounts are input and which are output is the
 *   server's `vatRole` tag and nothing else — no code ranges, no parent-account
 *   inference. `vatMapping` partitions the flat `accounts[]` on that tag alone.
 * - It does not sum. Both side totals and the net arrive computed and are passed
 *   through untouched.
 * - It does not re-sign. A line's `vatAmountTetri` is a directional movement and
 *   can legitimately be negative.
 * - It does not trust the draft's `validationState`. The settlement builder
 *   returns `postable: true` for a draft dated **inside a closed period**
 *   (measured); the entry form's own period gate is what decides, exactly as it
 *   does for a manual entry.
 */
export class VatService implements IVatService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: VatServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getVatReport(
        fromDate: string,
        toDate: string,
        options: VatReadOptions = DEFAULT_OPTIONS,
    ): Promise<ApiResponse<GetVatReportResponse>> {
        const result = await makeApiRequest<VatReportWire>(
            this.client.get(
                VatEndpoints.report({
                    dateFrom: fromDate,
                    dateTo: toDate,
                    locale: options.locale,
                }),
            ),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapVatReport(result.response);
        if (!mapped) {
            return UNMAPPABLE_REPORT;
        }
        return { success: true, response: mapped.report };
    }

    public async buildSettlement(
        body: BuildVatSettlementRequest,
    ): Promise<ApiResponse<PrepareVatSettlementResponse>> {
        // The body is passed as-is and carries no `source`: the server assigns
        // it, the journal's write edge rejects a supplied one, and this endpoint
        // ignores one silently. See `BuildVatSettlementRequest`.
        const result = await makeApiRequest<unknown>(
            this.client.post(VatEndpoints.settlement(), body),
        );
        if (!result.success) {
            return result;
        }
        return { success: true, response: mapJournalEntry(result.response) };
    }
}
